import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetDepartmentIdentityByIdResponse } from "src/api/Tmd.dtos";
import DepartmentIdentityContext from "src/contexts/data/DepartmentIdentityContext";
import useDepartmentIdentityById from "src/hooks/data/load/useDepartmentIdentityById";

/**
 * Configures the department identity request and the descendants receiving its state.
 */
interface IDepartmentProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<
        GetDepartmentIdentityByIdResponse,
        GetDepartmentIdentityByIdResponse
    >;
}

/**
 * Publishes the department identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const DepartmentIdentityProvider: FC<IDepartmentProviderProps> = ({
    children,
    id,
    configuration
}) => {
    const context = useDepartmentIdentityById(id, configuration);

    return (
        <DepartmentIdentityContext.Provider value={context}>
            {children}
        </DepartmentIdentityContext.Provider>
    );
};

/**
 * Loads department identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteDepartmentIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { departmentId } = useParams<{ departmentId: string }>();
    const id = departmentId ? parseInt(departmentId, 10) : undefined;

    return <DepartmentIdentityProvider id={id}>{children}</DepartmentIdentityProvider>;
};

export default DepartmentIdentityProvider;
